import React, { useMemo } from 'react';
import { Card, Descriptions, Tag, Alert, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { BankOutlined, FileTextOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getActualLoanData, getLoanApplicationReference, resolveEssPayload } from './essPayload';

interface ESSLoanDetailsViewProps {
    data: any;
    formikProps?: any;
}

const ESSLoanDetailsView: React.FC<ESSLoanDetailsViewProps> = ({ data, formikProps }) => {
    const actualData = getActualLoanData(data, formikProps);

    // Parse ESS request payload if available
    const essPayload = useMemo(() => {
        return resolveEssPayload(actualData);
    }, [actualData?.essRequestPayload, actualData?.formattedEssRequestPayload]);

    const isTakeoverLoan = actualData?.loanType === 'TAKEOVER' || actualData?.disbursementType === 'TAKEOVER';
    const { data: takeoverTracking } = useQuery({
        queryKey: ['/api/takeover-payments', actualData?.loanId, actualData?.branch],
        queryFn: async () => {
            const response = await axios.get(`/api/takeover-payments/${actualData.loanId}`, {
                params: { branch: actualData.branch }
            });
            return response.data;
        },
        enabled: Boolean(isTakeoverLoan && actualData?.loanId && actualData?.branch),
        staleTime: 30000
    });

    const isESSLoan =
        actualData?.checkNumber ||
        actualData?.applicationNumber ||
        actualData?.essRequestPayload ||
        actualData?.formattedEssRequestPayload;

    if (!isESSLoan) {
        return null;
    }

    const getLoanTypeColor = (type: string) => {
        switch (type) {
            case 'NEW': return 'blue';
            case 'TOPUP': return 'green';
            case 'RESTRUCTURE': return 'orange';
            case 'TAKEOVER': return 'purple';
            default: return 'default';
        }
    };

    return (
        <Card 
            title={
                <Space>
                    <BankOutlined />
                    <span>ESS Loan Integration Details</span>
                </Space>
            }
            className="mb-4"
        >
            <Alert
                message="This loan was initiated through the ESS (Electronic Scoring System) integration"
                type="info"
                icon={<InfoCircleOutlined />}
                showIcon
                className="mb-4"
            />

            <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                <Descriptions.Item label="ESS Application Number">
                    <strong>{getLoanApplicationReference(actualData, essPayload)}</strong>
                </Descriptions.Item>

                <Descriptions.Item label="Employee Check Number">
                    <strong>{actualData?.checkNumber || essPayload?.checkNumber || 'N/A'}</strong>
                </Descriptions.Item>

                <Descriptions.Item label="FSP Code">
                    {actualData?.essFspCode || essPayload?.fspCode || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Loan Type">
                    <Tag color={getLoanTypeColor(actualData?.loanType)}>
                        {actualData?.loanType || 'NEW'}
                    </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Product Code">
                    {essPayload?.productCode || actualData?.glNo || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Applied Through">
                    <Tag icon={<FileTextOutlined />} color="cyan">
                        E-MKOPO Gateway
                    </Tag>
                </Descriptions.Item>
            </Descriptions>

            {isTakeoverLoan && (
                <Card size="small" title="Takeover Approval & Transfer" className="mt-4">
                    <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }} size="small">
                        <Descriptions.Item label="Previous FSP">
                            {takeoverTracking?.previous_fsp_name || actualData?.previousFspName || essPayload?.fsp1Name || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Previous Loan">
                            {takeoverTracking?.previous_loan_number || actualData?.previousLoanNumber || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Payoff Amount">
                            {takeoverTracking?.payoffAmountToFsp1 != null ? takeoverTracking.payoffAmountToFsp1.toLocaleString() : 'Pending payoff response'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Transfer Status">
                            <Tag color={takeoverTracking?.transferStatus === 'COMPLETED' ? 'green' : 'orange'}>
                                {takeoverTracking?.transferStatus || 'PENDING'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="FSP1 Account">
                            {takeoverTracking?.fsp1_bank_account || actualData?.fsp1BankAccount || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Instruction Number">
                            {takeoverTracking?.transferInstructionNumber || 'Not generated'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Payment Reference">
                            {takeoverTracking?.transferReference || takeoverTracking?.fsp1_payment_reference || 'Not captured'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Acknowledgment">
                            {takeoverTracking?.paymentAcknowledgmentStatus || 'Pending'}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )}
        </Card>
    );
};

export default ESSLoanDetailsView;
